import React from "react";
import { AlertTriangle } from "lucide-react";
import { FaInstagram } from "react-icons/fa";
import { FORMAT_LABEL, InstagramAdState, formatWhen, rupees } from "./types";
import { InfoChip, SectionHeader, StepFooter } from "./ui";

const ReviewCard: React.FC<{ title: string; onEdit: () => void; children: React.ReactNode }> = ({ title, onEdit, children }) => (
  <div className="rounded-lg bg-white dark:bg-gray-900 px-4 py-4">
    <div className="flex items-center justify-between">
      <p className="text-base text-gray-900 dark:text-gray-100">{title}</p>
      <button type="button" onClick={onEdit} className="text-base text-primary-700 dark:text-primary-400 underline underline-offset-2">
        Edit Details
      </button>
    </div>
    {children}
  </div>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-4">{children}</div>
);

const Item: React.FC<{ label: string; span?: boolean; children: React.ReactNode }> = ({ label, span, children }) => (
  <div className={span ? "col-span-2" : ""}>
    <p className="text-sm text-gray-700 dark:text-gray-300">{label}</p>
    <div className="mt-2 flex flex-wrap gap-2">{children}</div>
  </div>
);

// "Mumbai" + 2 more → chip with a highlighted "+2"
const CountChip: React.FC<{ label: string; items: string[] }> = ({ label, items }) => {
  if (items.length === 0) return <InfoChip label={label} value="None" />;
  const rest = items.length - 1;
  return (
    <span className="inline-block text-sm bg-primary-50 dark:bg-primary-500/15 rounded px-2 py-1.5 text-gray-700 dark:text-gray-200">
      <span className="text-gray-900 dark:text-gray-100">{label}: </span>
      {items[0]}
      {rest > 0 && <span className="text-primary-700 dark:text-primary-300"> +{rest}</span>}
    </span>
  );
};

const StepReview: React.FC<{
  state: InstagramAdState;
  goToStep: (i: number) => void;
  onBack: () => void;
  onLaunch: () => void;
  validating: boolean;
  validationErrors: string[] | null;
  launching: boolean;
}> = ({ state, goToStep, onBack, onLaunch, validating, validationErrors, launching }) => {
  const first = state.adSets[0];
  const { budget } = state;
  const category = state.specialCategory || "None";
  const buying = state.buyingType === "auction" ? "Auction" : "Reach and Frequency";
  const placement = first.placementMode === "optimised" ? "Optimised Placements" : `Manual Placements (${first.manualPlacements.length})`;
  const hasErrors = !!validationErrors && validationErrors.length > 0;

  return (
    <div>
      <SectionHeader title="Review and Launch" subtitle="Preview and Launch your broadcast" />

      {validating && <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Checking this broadcast is ready to launch…</p>}
      {hasErrors && (
        <div className="mb-4 rounded-lg border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-medium text-rose-700 dark:text-rose-300">
            <AlertTriangle size={16} /> Fix these before launching
          </p>
          <ul className="mt-1.5 list-disc list-inside text-sm text-rose-700 dark:text-rose-300">
            {validationErrors!.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        <ReviewCard title="Broadcast Details" onEdit={() => goToStep(2)}>
          <Grid>
            <Item label="Channel">
              <span className="inline-flex items-center gap-1.5 text-sm px-1.5 py-0.5 text-gray-900 dark:text-gray-100">
                <span className="w-5 h-5 rounded bg-gradient-to-br from-fuchsia-200 to-pink-100 flex items-center justify-center">
                  <FaInstagram size={12} className="text-pink-600" />
                </span>
                Instagram
              </span>
            </Item>
            <Item label="Sender Profile">
              <span className="inline-flex items-center gap-1.5 text-sm px-1.5 py-0.5 text-gray-900 dark:text-gray-100">
                <span className="w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] font-semibold flex items-center justify-center">V</span>
                {state.senderHandle || "No sender selected"}
              </span>
            </Item>
            <Item label="Broadcast Goal">
              <InfoChip value={state.goal} />
            </Item>
            <Item label="Broadcast Name">
              <InfoChip value={state.name || "—"} />
            </Item>
            <Item label="Buying Type">
              <InfoChip value={buying} />
            </Item>
            <Item label="Special ad Category">
              <InfoChip value={category} />
            </Item>
          </Grid>
        </ReviewCard>

        <ReviewCard title="Ad Set Details" onEdit={() => goToStep(2)}>
          <Grid>
            <Item label="Total Ad Sets">
              <InfoChip value={String(state.adSets.length)} />
            </Item>
            <Item label="Ad Set Name">
              {state.adSets.map((a) => (
                <InfoChip key={a.id} value={a.name || "New Ad Set"} />
              ))}
            </Item>
            <Item label="Audience" span>
              <CountChip label="Locations" items={first.locations.map((l) => l.name)} />
              <CountChip label="Interests" items={first.interests.map((i) => i.name)} />
            </Item>
            <Item label="Ad Placement">
              <InfoChip value={placement} />
            </Item>
            <Item label="Buying Type">
              <InfoChip value={buying} />
            </Item>
            <Item label="Special ad Category">
              <InfoChip value={category} />
            </Item>
          </Grid>
        </ReviewCard>

        <ReviewCard title="Messaging" onEdit={() => goToStep(3)}>
          <div className="mt-3">
            <InfoChip value={`${FORMAT_LABEL[state.creative.format]} Ad`} />
          </div>
        </ReviewCard>

        <ReviewCard title="Budget and Schedule" onEdit={() => goToStep(4)}>
          <Grid>
            <Item label="Budget Type">
              <InfoChip value={budget.budgetType} />
            </Item>
            <Item label="Budget">
              <InfoChip value={rupees(budget.amount)} />
            </Item>
            <Item label="Ad Start Time">
              <InfoChip value={formatWhen(budget.startAt)} />
            </Item>
            <Item label="Ad End Time">
              <InfoChip value={budget.hasEnd ? formatWhen(budget.endAt) : "Not set"} />
            </Item>
            <Item label="Time Zone">
              <InfoChip value={budget.timezone} />
            </Item>
            <Item label="Status">
              <span className="inline-flex items-center gap-1.5 text-sm bg-emerald-100 dark:bg-emerald-500/15 text-gray-800 dark:text-emerald-300 rounded px-2 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
              </span>
            </Item>
          </Grid>
        </ReviewCard>
      </div>

      <StepFooter onBack={onBack} onNext={onLaunch} nextLabel={launching ? "Launching…" : "Launch"} nextDisabled={launching || (validationErrors !== null && hasErrors)} />
    </div>
  );
};

export default StepReview;
